/**
 * Morocco moved to permanent UTC+0 at 2026-09-20T01:00Z (tzdata 2026c). A
 * runtime whose IANA table predates that — Chrome 153 did, measured
 * 2026-09-22 — still renders `Africa/Casablanca` at UTC+1, so a pass stored
 * correctly for 14:00 reads 15:00 in one browser and 14:00 in the next.
 *
 * Storage is not affected: clients send the wall clock and the server anchors
 * it (see `authoredInstant`). Display is, because every client formats through
 * its own `Intl`. This shim repairs display on a stale runtime by rendering
 * Casablanca instants from the cutover onward as UTC, which is what the zone
 * now is. Earlier instants keep the runtime's own table, which has them right.
 *
 * It installs only when the runtime is detected as stale, so it goes inert on
 * its own once browsers and Hermes ship 2026c. Delete it when that is true of
 * the runtimes we support.
 */

const ZONE = "Africa/Casablanca";
const CUTOVER_MS = Date.UTC(2026, 8, 20, 1, 0);
/** Any instant after the cutover: 12:00Z on 2026-10-01 must read 12h. */
const PROBE_MS = Date.UTC(2026, 9, 1, 12, 0);

type NativeDTF = typeof Intl.DateTimeFormat;
type DateInput = Date | number | undefined;

/** True when `Intl` still places Casablanca at UTC+1 after the cutover. */
export function hasStaleCasablancaTzdata(
  DTF: NativeDTF = Intl.DateTimeFormat,
): boolean {
  try {
    const hour = new DTF("en-US", {
      timeZone: ZONE,
      hour: "numeric",
      hourCycle: "h23",
    })
      .formatToParts(PROBE_MS)
      .find((p) => p.type === "hour")?.value;
    return hour !== undefined && Number(hour) !== 12;
  } catch {
    return false;
  }
}

function toMs(d: DateInput): number {
  if (d === undefined) return Date.now();
  return typeof d === "number" ? d : d.getTime();
}

/**
 * A DateTimeFormat constructor that behaves like `Native` except for formats
 * resolving to `Africa/Casablanca` (named, or the device default): those
 * render instants at or after the cutover through a UTC twin. A range is
 * routed by its start.
 */
export function casablancaCorrectedDateTimeFormat(
  Native: NativeDTF,
): NativeDTF {
  function DateTimeFormat(
    locales?: string | string[],
    options?: Intl.DateTimeFormatOptions,
  ): Intl.DateTimeFormat {
    const native = new Native(locales, options);
    if (native.resolvedOptions().timeZone !== ZONE) return native;
    const utc = new Native(locales, { ...options, timeZone: "UTC" });
    const pick = (d: DateInput) => (toMs(d) >= CUTOVER_MS ? utc : native);

    const corrected = Object.create(Native.prototype) as Intl.DateTimeFormat;
    Object.defineProperties(corrected, {
      format: { value: (d?: DateInput) => pick(d).format(d) },
      formatToParts: { value: (d?: DateInput) => pick(d).formatToParts(d) },
      formatRange: {
        value: (a: Date | number, b: Date | number) =>
          pick(a).formatRange(a, b),
      },
      formatRangeToParts: {
        value: (a: Date | number, b: Date | number) =>
          pick(a).formatRangeToParts(a, b),
      },
      resolvedOptions: { value: () => native.resolvedOptions() },
    });
    return corrected;
  }
  DateTimeFormat.prototype = Native.prototype;
  DateTimeFormat.supportedLocalesOf = Native.supportedLocalesOf.bind(Native);
  return DateTimeFormat as unknown as NativeDTF;
}

const DATE_FIELDS = ["weekday", "year", "month", "day"] as const;
const TIME_FIELDS = [
  "dayPeriod",
  "hour",
  "minute",
  "second",
  "fractionalSecondDigits",
] as const;

/** The defaults `Date.prototype.toLocale*String` add when the caller names
 *  none of the fields they care about (ECMA-402 ToDateTimeOptions). */
function withDefaults(
  options: Intl.DateTimeFormatOptions | undefined,
  required: "date" | "time" | "any",
): Intl.DateTimeFormatOptions {
  const o: Intl.DateTimeFormatOptions = { ...options };
  if (o.dateStyle !== undefined || o.timeStyle !== undefined) return o;
  const has = (fields: readonly string[]) =>
    fields.some((f) => (o as Record<string, unknown>)[f] !== undefined);
  const needsDate = required !== "time";
  const needsTime = required !== "date";
  if ((needsDate && has(DATE_FIELDS)) || (needsTime && has(TIME_FIELDS))) {
    return o;
  }
  if (needsDate)
    Object.assign(o, { year: "numeric", month: "numeric", day: "numeric" });
  if (needsTime)
    Object.assign(o, { hour: "numeric", minute: "numeric", second: "numeric" });
  return o;
}

interface ShimTarget {
  Intl: { DateTimeFormat: NativeDTF };
  Date: { prototype: Date };
}

const installedOn = new WeakSet<ShimTarget>();

/**
 * Installs the correction on `Intl.DateTimeFormat` and the three
 * `Date.prototype.toLocale*String` methods — only on a stale runtime, at most
 * once. Call it before any module builds a DateTimeFormat: import it first
 * from the app entry. `target` is for tests. Returns whether it installed.
 */
export function installStaleTzdataShim(
  target: ShimTarget = globalThis,
): boolean {
  if (installedOn.has(target)) return false;
  const Native = target.Intl.DateTimeFormat;
  if (!hasStaleCasablancaTzdata(Native)) return false;
  try {
    const Corrected = casablancaCorrectedDateTimeFormat(Native);
    const proto = target.Date.prototype;
    // Captured unbound on purpose: each is re-called below with `.call(this)`.
    /* eslint-disable @typescript-eslint/unbound-method */
    const methods = {
      toLocaleString: [proto.toLocaleString, "any"],
      toLocaleDateString: [proto.toLocaleDateString, "date"],
      toLocaleTimeString: [proto.toLocaleTimeString, "time"],
    } as const;
    /* eslint-enable @typescript-eslint/unbound-method */

    for (const [name, [original, required]] of Object.entries(methods)) {
      Object.defineProperty(proto, name, {
        configurable: true,
        writable: true,
        value(
          this: Date,
          locales?: string | string[],
          options?: Intl.DateTimeFormatOptions,
        ): string {
          const ms = this.getTime();
          if (Number.isNaN(ms) || ms < CUTOVER_MS) {
            return original.call(this, locales, options);
          }
          return new Corrected(locales, withDefaults(options, required)).format(
            ms,
          );
        },
      });
    }
    target.Intl.DateTimeFormat = Corrected;
    installedOn.add(target);
    return true;
  } catch {
    // A runtime that refuses the patch keeps its own (stale) rendering.
    return false;
  }
}
